// Double Ratchet — mesaj şifreleme (9.34 · D-605).
//
// X3DH tek bir ortak sır verir; Double Ratchet onu HER MESAJ İÇİN yeni bir
// anahtara çevirir. İki mekanizma iç içe: (1) DH ratchet — karşı taraf yeni bir
// ratchet açık anahtarı gönderdiğinde kök anahtar yenilenir (kendini iyileştirme);
// (2) simetrik zincir — her mesaj zinciri bir adım ilerletip tek-kullanımlık bir
// mesaj anahtarı üretir (ileriye dönük gizlilik).
//
// Kripto matematiği @noble'da (X25519, HKDF, HMAC-SHA256, XChaCha20-Poly1305);
// burada Signal spesifikasyonunun DURUM MAKİNESİ var — protokolü biz üstlendik
// (D-605), o yüzden bu dosya en çok testli olan.
import { x25519 } from '@noble/curves/ed25519.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { hmac } from '@noble/hashes/hmac.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { xchacha20poly1305 } from '@noble/ciphers/chacha.js';
import { base64ToBytes, bytesToBase64, bytesToUtf8, utf8ToBytes } from './crypto';

// Sırasız teslimde saklanacak azami atlanmış mesaj anahtarı. Sınırsız olsaydı
// kötü niyetli devasa `n` bellek tüketirdi.
const MAX_SKIP = 1000;
const INFO_RK = utf8ToBytes('nosubject-ratchet-rk');
const INFO_MK = utf8ToBytes('nosubject-ratchet-mk');

type KeyPair = { pub: string; sec: string };

// Tümü base64 → durum serileştirilebilir (ileride güvenli depoya yazılacak).
export type RatchetState = {
  DHs: KeyPair;        // kendi güncel ratchet çiftimiz
  DHr: string | null;  // karşının güncel ratchet açık anahtarı
  RK: string;          // kök anahtar
  CKs: string | null;  // gönderme zinciri anahtarı
  CKr: string | null;  // alma zinciri anahtarı
  Ns: number;          // bu gönderme zincirinde gönderilen mesaj sayısı
  Nr: number;          // bu alma zincirinde alınan mesaj sayısı
  PN: number;          // önceki gönderme zincirinin uzunluğu
  skipped: Record<string, string>; // "dhPub:n" → mesaj anahtarı (b64)
};

export type Header = { dh: string; pn: number; n: number };
export type RatchetMessage = { header: Header; ciphertext: string };

function concat(arrays: Uint8Array[]): Uint8Array {
  const total = arrays.reduce((n, a) => n + a.length, 0);
  const out = new Uint8Array(total);
  let o = 0;
  for (const a of arrays) { out.set(a, o); o += a.length; }
  return out;
}

function dh(sec: string, pub: string): Uint8Array {
  return x25519.getSharedSecret(base64ToBytes(sec), base64ToBytes(pub));
}

// KDF_RK: kök anahtarı tuz, DH çıktısını girdi alıp 64 bayt türetir → yeni kök +
// zincir anahtarı.
function kdfRK(rk: string, dhOut: Uint8Array): { rk: string; ck: string } {
  const out = hkdf(sha256, dhOut, base64ToBytes(rk), INFO_RK, 64);
  return { rk: bytesToBase64(out.slice(0, 32)), ck: bytesToBase64(out.slice(32, 64)) };
}

// KDF_CK: zincir anahtarını HMAC ile ilerletir; 0x01 → mesaj anahtarı, 0x02 →
// sonraki zincir anahtarı. Tek yönlü: mesaj anahtarından zincir geri çözülemez.
function kdfCK(ck: string): { ck: string; mk: string } {
  const key = base64ToBytes(ck);
  const mk = hmac(sha256, key, Uint8Array.of(0x01));
  const nextCk = hmac(sha256, key, Uint8Array.of(0x02));
  return { ck: bytesToBase64(nextCk), mk: bytesToBase64(mk) };
}

function newRatchetKey(): KeyPair {
  const k = x25519.keygen();
  return { pub: bytesToBase64(k.publicKey), sec: bytesToBase64(k.secretKey) };
}

function headerBytes(h: Header): Uint8Array {
  const nums = new Uint8Array(8);
  const view = new DataView(nums.buffer);
  view.setUint32(0, h.pn);
  view.setUint32(4, h.n);
  return concat([base64ToBytes(h.dh), nums]);
}

// Mesaj anahtarından AEAD anahtarı + nonce türet, sonra XChaCha20-Poly1305.
// Başlık, ilişkilendirilmiş veriye (baseAD || header) bağlanır: başlık
// kurcalanırsa çözme başarısız olur.
function aeadKey(mk: string): { key: Uint8Array; nonce: Uint8Array } {
  const km = hkdf(sha256, base64ToBytes(mk), new Uint8Array(32), INFO_MK, 56);
  return { key: km.slice(0, 32), nonce: km.slice(32, 56) };
}
function seal(mk: string, plaintext: Uint8Array, ad: Uint8Array): Uint8Array {
  const { key, nonce } = aeadKey(mk);
  return xchacha20poly1305(key, nonce, ad).encrypt(plaintext);
}
function open(mk: string, ciphertext: Uint8Array, ad: Uint8Array): Uint8Array {
  const { key, nonce } = aeadKey(mk);
  return xchacha20poly1305(key, nonce, ad).decrypt(ciphertext);
}

// Başlatan (Alice): X3DH sonrası hemen bir DH ratchet adımı atar. İlk DHr, Bob'un
// X3DH'de kullanılan imzalı prekey AÇIK anahtarıdır.
export function initInitiator(sharedSecret: string, peerSignedPreKeyPublic: string): RatchetState {
  const DHs = newRatchetKey();
  const { rk, ck } = kdfRK(sharedSecret, dh(DHs.sec, peerSignedPreKeyPublic));
  return { DHs, DHr: peerSignedPreKeyPublic, RK: rk, CKs: ck, CKr: null, Ns: 0, Nr: 0, PN: 0, skipped: {} };
}

// Alıcı (Bob): kök anahtar X3DH sırrıdır; ilk ratchet çifti, X3DH'de kullanılan
// imzalı prekey çiftidir. DHr, Alice'in ilk mesajıyla öğrenilir.
export function initResponder(sharedSecret: string, signedPreKeyPair: KeyPair): RatchetState {
  return { DHs: signedPreKeyPair, DHr: null, RK: sharedSecret, CKs: null, CKr: null, Ns: 0, Nr: 0, PN: 0, skipped: {} };
}

export function ratchetEncrypt(state: RatchetState, plaintext: string, baseAD: string): RatchetMessage {
  if (state.CKs === null) throw new Error('Gönderme zinciri yok');
  const { ck, mk } = kdfCK(state.CKs);
  state.CKs = ck;
  const header: Header = { dh: state.DHs.pub, pn: state.PN, n: state.Ns };
  state.Ns += 1;
  const ad = concat([base64ToBytes(baseAD), headerBytes(header)]);
  const ct = seal(mk, utf8ToBytes(plaintext), ad);
  return { header, ciphertext: bytesToBase64(ct) };
}

function trySkipped(state: RatchetState, message: RatchetMessage, baseAD: string): Uint8Array | null {
  const key = `${message.header.dh}:${message.header.n}`;
  const mk = state.skipped[key];
  if (!mk) return null;
  delete state.skipped[key];
  const ad = concat([base64ToBytes(baseAD), headerBytes(message.header)]);
  return open(mk, base64ToBytes(message.ciphertext), ad);
}

// Mevcut alma zincirini `until`'e kadar ilerletip atlanan mesaj anahtarlarını
// saklar — böylece sırasız gelen eski mesaj sonra çözülebilir.
function skipMessageKeys(state: RatchetState, until: number): void {
  if (state.CKr === null) return;
  if (state.Nr + MAX_SKIP < until) throw new Error('Çok fazla atlanmış mesaj');
  while (state.Nr < until) {
    const { ck, mk } = kdfCK(state.CKr);
    state.CKr = ck;
    state.skipped[`${state.DHr}:${state.Nr}`] = mk;
    state.Nr += 1;
  }
}

// DH ratchet adımı: karşı taraf yeni ratchet anahtarı gönderdi. Önce alma
// zinciri (onların yeni anahtarıyla), sonra kendi yeni anahtarımızla gönderme
// zinciri türetilir; kök anahtar iki kez ilerler.
function dhRatchet(state: RatchetState, header: Header): void {
  state.PN = state.Ns;
  state.Ns = 0;
  state.Nr = 0;
  state.DHr = header.dh;
  const recv = kdfRK(state.RK, dh(state.DHs.sec, state.DHr));
  state.RK = recv.rk;
  state.CKr = recv.ck;
  state.DHs = newRatchetKey();
  const send = kdfRK(state.RK, dh(state.DHs.sec, state.DHr));
  state.RK = send.rk;
  state.CKs = send.ck;
}

export function ratchetDecrypt(state: RatchetState, message: RatchetMessage, baseAD: string): string {
  const skipped = trySkipped(state, message, baseAD);
  if (skipped) return bytesToUtf8(skipped);

  if (state.DHr === null || message.header.dh !== state.DHr) {
    skipMessageKeys(state, message.header.pn);
    dhRatchet(state, message.header);
  }
  skipMessageKeys(state, message.header.n);

  if (state.CKr === null) throw new Error('Alma zinciri yok');
  const { ck, mk } = kdfCK(state.CKr);
  state.CKr = ck;
  state.Nr += 1;
  const ad = concat([base64ToBytes(baseAD), headerBytes(message.header)]);
  return bytesToUtf8(open(mk, base64ToBytes(message.ciphertext), ad));
}
